/* @jsx h */
import { defineComponent, h, ref, computed, onMounted } from "vue";
import { useMenu } from "@app/vue/providers/index";
import { RouterLink } from "vue-router";
import { EventSummary } from "@lemmonhq/types";
import { fetchShopEvents } from "@app/api/client";
import { Lightbox, useLocalization } from "@lemmonhq/uikit";
import { getNextEventOccurrence } from "../../../utils/eventSchedule";

export const EventsPage = defineComponent({
  name: "EventsPage",
  setup() {
    const { shop } = useMenu();
    const { locale, t } = useLocalization();
    const events = ref<EventSummary[]>([]);
    const loading = ref(true);
    const fullImageEvent = ref<EventSummary | null>(null);

    onMounted(async () => {
      try {
        events.value = await fetchShopEvents(shop.value.slug);
        loading.value = false;
      } catch (error) {
        console.error("Failed to load events:", error);
        events.value = [];
        loading.value = false;
      }
    });

    // Keep only events with an upcoming occurrence, sorted by that occurrence.
    // For recurring events this is the next future date from the rrule rather
    // than the (often past) base `date`/`start_at`.
    const upcomingEvents = computed(() => {
      const now = new Date();
      return events.value
        .map((event) => ({ event, next: getNextEventOccurrence(event, now) }))
        .filter(
          (e): e is { event: EventSummary; next: Date } => e.next !== null,
        )
        .sort((a, b) => a.next.getTime() - b.next.getTime());
    });

    const formatDate = (date: Date) =>
      date.toLocaleDateString(locale.value || "en", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });

    const formatTime = (date: Date) =>
      date.toLocaleTimeString(locale.value || "en", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

    const getEventUrl = (eventId: string) =>
      `/restaurant/${shop.value.slug}/reserve/events/${eventId}`;

    return () => (
      <div class="space-y-6">
        {loading.value ? (
          <div
            class="flex justify-center py-12"
            role="status"
            aria-live="polite"
          >
            <span class="loading loading-spinner loading-md"></span>
            <span class="sr-only">
              {t("customer.reserve_events.loading_events")}
            </span>
          </div>
        ) : upcomingEvents.value.length === 0 ? (
          <div class="py-16 text-center">
            <span
              class="material-symbols-outlined text-5xl text-gray-300 mb-3 block"
              aria-hidden="true"
            >
              event_busy
            </span>
            <p class="text-gray-500 font-light text-lg">
              {t("customer.reserve_events.no_upcoming_events")}
            </p>
            <p class="text-gray-400 font-light text-sm mt-1">
              {t("customer.reserve_events.check_back_later")}
            </p>
          </div>
        ) : (
          <>
            {/* Featured Event -- first event gets hero treatment */}
            {upcomingEvents.value[0] && (
              <div
                class="group relative rounded-2xl motion-safe:hover:-translate-y-0.5 hover:shadow-xl"
                style="transition: transform 300ms var(--ease-spring), box-shadow 300ms ease;"
              >
                <RouterLink
                  to={getEventUrl(upcomingEvents.value[0].event.id)}
                  class="w-full block text-left rounded-2xl overflow-hidden relative"
                  aria-label={t("customer.reserve_events.view_event", [
                    upcomingEvents.value[0].event.title || "",
                  ])}
                >
                  <div class="aspect-[21/9] md:aspect-[3/1] overflow-hidden bg-gray-100">
                    {upcomingEvents.value[0].event.image && (
                      <img
                        src={upcomingEvents.value[0].event.image}
                        alt={upcomingEvents.value[0].event.title}
                        class="w-full h-full object-cover motion-safe:group-hover:scale-105"
                        style="transition: transform 600ms var(--ease-spring);"
                        onError={(e: Event) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                  </div>
                  {/* Gradient overlay with event info */}
                  <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex flex-col justify-end p-6 md:p-8">
                    <div class="flex flex-wrap items-center gap-2 mb-2">
                      <span
                        class="text-xs font-medium backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1 min-h-[32px] whitespace-nowrap"
                        style="color: rgba(255,255,255,0.9); background: rgba(0,0,0,0.4);"
                      >
                        <span
                          class="material-symbols-outlined text-xs"
                          aria-hidden="true"
                        >
                          calendar_today
                        </span>
                        {formatDate(upcomingEvents.value[0].next)}
                      </span>
                      <span
                        class="text-xs font-medium backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1 min-h-[32px] whitespace-nowrap"
                        style="color: rgba(255,255,255,0.9); background: rgba(0,0,0,0.4);"
                      >
                        <span
                          class="material-symbols-outlined text-xs"
                          aria-hidden="true"
                        >
                          schedule
                        </span>
                        {formatTime(upcomingEvents.value[0].next)}
                      </span>
                    </div>
                    <h3 class="text-2xl md:text-3xl font-semibold text-white mb-1">
                      {upcomingEvents.value[0].event.title}
                    </h3>
                    {upcomingEvents.value[0].event.description && (
                      <p
                        class="text-sm font-light line-clamp-2 max-w-lg"
                        style="color: rgba(255,255,255,0.85);"
                      >
                        {upcomingEvents.value[0].event.description}
                      </p>
                    )}
                  </div>
                </RouterLink>
                {upcomingEvents.value[0].event.image && (
                  <button
                    type="button"
                    class="absolute top-3 right-3 flex items-center justify-center w-9 h-9 rounded-full backdrop-blur-sm cursor-zoom-in"
                    style="color: rgba(255,255,255,0.95); background: rgba(0,0,0,0.45);"
                    aria-label={t("customer.reserve_events.view_full_image")}
                    onClick={() =>
                      (fullImageEvent.value = upcomingEvents.value[0].event)
                    }
                  >
                    <span
                      class="material-symbols-outlined text-lg"
                      aria-hidden="true"
                    >
                      open_in_full
                    </span>
                  </button>
                )}
              </div>
            )}
            {fullImageEvent.value?.image && (
              <Lightbox
                src={fullImageEvent.value.image}
                alt={fullImageEvent.value.title}
                closeLabel={t("customer.reserve_events.close_image")}
                onClose={() => (fullImageEvent.value = null)}
              />
            )}

            {/* Remaining events -- horizontal card list */}
            {upcomingEvents.value.length > 1 && (
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upcomingEvents.value.slice(1).map(({ event, next }) => (
                  <RouterLink
                    key={event.id}
                    to={getEventUrl(event.id)}
                    class="group text-left rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-sm bg-white flex"
                    style="transition: border-color 200ms ease, box-shadow 200ms ease;"
                    aria-label={t("customer.reserve_events.view_event", [
                      event.title,
                    ])}
                  >
                    {event.image && (
                      <div class="w-32 md:w-40 flex-shrink-0 overflow-hidden bg-gray-100">
                        <img
                          src={event.image}
                          alt={event.title}
                          class="w-full h-full object-cover motion-safe:group-hover:scale-105"
                          style="transition: transform 500ms var(--ease-spring);"
                          onError={(e: Event) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      </div>
                    )}
                    <div class="flex-1 p-4 flex flex-col justify-center min-w-0">
                      <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 mb-1.5">
                        <span class="inline-flex items-center gap-1 whitespace-nowrap">
                          <span
                            class="material-symbols-outlined text-xs"
                            aria-hidden="true"
                          >
                            calendar_today
                          </span>
                          {formatDate(next)}
                        </span>
                        <span class="inline-flex items-center gap-1 whitespace-nowrap">
                          <span
                            class="material-symbols-outlined text-xs"
                            aria-hidden="true"
                          >
                            schedule
                          </span>
                          {formatTime(next)}
                        </span>
                      </div>
                      <h3
                        class="text-base font-medium text-gray-900 group-hover:text-gray-700 truncate"
                        style="transition: color 200ms ease;"
                      >
                        {event.title}
                      </h3>
                      {event.description && (
                        <p class="text-sm text-gray-500 font-light line-clamp-1 mt-0.5">
                          {event.description}
                        </p>
                      )}
                    </div>
                  </RouterLink>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  },
});
